import React, { useState, useEffect } from 'react';
import { authFetch } from '../utils/authFetch';
import { useUser } from '../context/UserContext';

interface Setting {
  id: string;
  key: string;
  value: string;
  value_type: string;
  description: string;
  category: string;
  is_public: boolean;
  created_at: string;
  updated_at: string;
  updated_by?: { id: string; name?: string } | null;
}

interface SettingsResponse {
  code: number;
  message: string;
  data: Setting[];
}

const Settings: React.FC = () => {
  const { accessToken, setUserAndToken } = useUser();
  const [settings, setSettings] = useState<Setting[]>([]);
  const [filteredSettings, setFilteredSettings] = useState<Setting[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [formKey, setFormKey] = useState<string>('');
  const [formValue, setFormValue] = useState<string>('');
  const [formValueType, setFormValueType] = useState<string>('string');
  const [formDescription, setFormDescription] = useState<string>('');
  const [formCategory, setFormCategory] = useState<string>('general');
  const [formIsPublic, setFormIsPublic] = useState<boolean>(true);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await authFetch(
        (process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000') + '/api/v1/settings',
        {},
        accessToken,
        setUserAndToken
      );
      const data: SettingsResponse = await response.json();
      
      if (data.code === 200) {
        const sorted = [...data.data].sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
        setSettings(sorted);
        setFilteredSettings(sorted);
      } else {
        setError(data.message || 'Failed to fetch settings');
      }
    } catch (err) {
      setError('Failed to fetch settings');
      console.error('Error fetching settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setModalMode('create');
    setFormKey('');
    setFormValue('');
    setFormValueType('string');
    setFormDescription('');
    setFormCategory('general');
    setFormIsPublic(true);
    setIsModalOpen(true);
  };

  const openEditModal = (setting: Setting) => {
    setModalMode('edit');
    setFormKey(setting.key);
    setFormValue(setting.value);
    setFormValueType(setting.value_type);
    setFormDescription(setting.description);
    setFormCategory(setting.category);
    setFormIsPublic(setting.is_public);
    setIsModalOpen(true);
  };

  const submitModal = async (setting?: Setting) => {
    try {
      setError(null);
      let resp: Response | null = null;
      if (modalMode === 'create') {
        resp = await authFetch(
          (process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000') + `/api/v1/settings`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              key: formKey,
              value: formValue,
              value_type: formValueType,
              description: formDescription,
              category: formCategory,
              is_public: formIsPublic,
            }),
          },
          accessToken,
          setUserAndToken
        );
      } else if (setting) {
        resp = await authFetch(
          (process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000') + `/api/v1/settings/${setting.key}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              value: formValue,
              value_type: formValueType,
              description: formDescription,
              category: formCategory,
              is_public: formIsPublic,
            }),
          },
          accessToken,
          setUserAndToken
        );
      }
      if (!resp) {
        throw new Error('No response from server');
      }
      const data = await resp.json();
      if (data.code !== 200 && data.code !== 201) throw new Error(data.message || 'Failed to save setting');
      setIsModalOpen(false);
      await fetchSettings();
    } catch (err) {
      setError('Failed to save setting');
    }
  };

  const closeModal = () => setIsModalOpen(false);

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    if (!term.trim()) {
      setFilteredSettings(settings);
    } else {
      const filtered = settings.filter(setting =>
        setting.key.toLowerCase().includes(term.toLowerCase()) ||
        (setting.description && setting.description.toLowerCase().includes(term.toLowerCase()))
      );
      setFilteredSettings(filtered);
    }
  };

  const renderValueCell = (setting: Setting) => (
    <span className="text-gray-700">
      {setting.value_type === 'boolean' ? (setting.value === 'true' ? 'Yes' : 'No') : setting.value}
    </span>
  );

  const renderActionButtons = (setting: Setting) => (
    <button
      onClick={() => openEditModal(setting)}
      className="bg-blue-500 text-white px-3 py-1 rounded text-sm hover:bg-blue-600"
    >
      Edit
    </button>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg">Loading settings...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-red-500 text-lg">{error}</div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg text-slate-900 mb-1 px-2">Manage application settings and configuration</h2>
        </div>
      </div>
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 className="text-lg text-slate-900">Application Settings</h3>
          <div className="flex items-center gap-2">
            <button onClick={openCreateModal} className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700">New Setting</button>
            <div className="relative">
              <input
                type="text"
                placeholder="Search settings..."
                className="pl-4 pr-10 py-2 rounded-lg border border-slate-200 bg-slate-50 text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-100 text-sm"
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
              />
              <span className="absolute right-3 top-2.5 text-slate-400">
                <svg width="16" height="16" fill="none" viewBox="0 0 24 24"><path stroke="currentColor" strokeWidth="2" d="M21 21l-4.35-4.35m2.1-5.4a7.5 7.5 0 11-15 0 7.5 7.5 0 0115 0z"/></svg>
              </span>
            </div>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-slate-500 border-b border-slate-100">
                <th className="px-6 py-3 text-left font-semibold">SETTING</th>
                <th className="px-6 py-3 text-left font-semibold">VALUE</th>
                <th className="px-6 py-3 text-left font-semibold">TYPE</th>
                <th className="px-6 py-3 text-left font-semibold">CATEGORY</th>
                <th className="px-6 py-3 text-left font-semibold">DESCRIPTION</th>
                <th className="px-6 py-3 text-left font-semibold">UPDATED BY</th>
                <th className="px-6 py-3 text-left font-semibold">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filteredSettings.map((setting) => (
                <tr key={setting.key} className="border-b border-slate-50 hover:bg-slate-50 transition">
                  <td className="px-6 py-4 text-left whitespace-nowrap">
                    <div className="font-semibold text-slate-900">{setting.key}</div>
                  </td>
                  <td className="px-6 py-4 text-left">
                    <div className="max-w-96 break-words">
                      {renderValueCell(setting)}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-left whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                      {setting.value_type}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-left whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                      {setting.category}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-left">
                    <div className="text-slate-700 max-w-xs">{setting.description}</div>
                  </td>
                <td className="px-6 py-4 text-left whitespace-nowrap">
                  <div className="flex flex-col">
                    <span className="text-slate-700">
                      {setting.updated_by ? (setting.updated_by.name || setting.updated_by.id) : '-'}
                    </span>
                    <span className="text-slate-400 text-xs mt-0.5">
                      {setting.updated_at ? new Date(setting.updated_at).toLocaleString() : ''}
                    </span>
                  </div>
                </td>
                  <td className="px-6 py-4 text-left whitespace-nowrap">
                    {renderActionButtons(setting)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-3 text-slate-500 text-sm border-t border-slate-100">
          Showing 1 to {filteredSettings.length} of {settings.length} results
        </div>
      </div>
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-semibold text-slate-900">{modalMode === 'create' ? 'Add Setting' : 'Edit Setting'}</h4>
              <button onClick={closeModal} className="text-slate-500 hover:text-slate-700">✕</button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-3">
                <label className="text-sm text-slate-700">Key</label>
                <input type="text" value={formKey} onChange={(e) => setFormKey(e.target.value)} disabled={modalMode==='edit'} className="border border-slate-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-100" />
              </div>
              <div className="grid grid-cols-1 gap-3">
                <label className="text-sm text-slate-700">Value</label>
                <input type={formValueType==='integer'?'number':'text'} value={formValue} onChange={(e)=>setFormValue(e.target.value)} className="border border-slate-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-100" />
              </div>
              <div className="grid grid-cols-1 gap-3">
                <label className="text-sm text-slate-700">Type</label>
                <div className="relative">
                  <select value={formValueType} onChange={(e)=>setFormValueType(e.target.value)} className="border border-slate-200 rounded px-3 py-2 pr-8 focus:outline-none focus:ring-2 focus:ring-blue-100 appearance-none w-full">
                    <option value="string">string</option>
                    <option value="integer">integer</option>
                    <option value="boolean">boolean</option>
                    <option value="json_array">json_array</option>
                  </select>
                  {/* Custom dropdown arrow */}
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3">
                <label className="text-sm text-slate-700">Description</label>
                <textarea value={formDescription} onChange={(e)=>setFormDescription(e.target.value)} rows={4} className="border border-slate-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-100 resize-y" />
              </div>
              <div className="grid grid-cols-1 gap-3">
                <label className="text-sm text-slate-700">Category</label>
                <input type="text" value={formCategory} onChange={(e)=>setFormCategory(e.target.value)} className="border border-slate-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-100" />
              </div>
              <div className="flex items-center gap-2">
                <input id="is_public" type="checkbox" checked={formIsPublic} onChange={(e)=>setFormIsPublic(e.target.checked)} className="h-4 w-4" />
                <label htmlFor="is_public" className="text-sm text-slate-700">Public</label>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={closeModal} className="px-4 py-2 rounded bg-slate-200 text-slate-800">Cancel</button>
              <button onClick={()=>submitModal(settings.find(s=>s.key===formKey))} className="px-4 py-2 rounded bg-blue-600 text-white">{modalMode==='create'?'Create':'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings; 